import React from 'react';
import { Checkbox, Tooltip } from '@mui/material';
import { MRT_TableInstance, MRT_Row, MRT_ColumnDef } from 'material-react-table';
import { CreateCustomRowSelectionColumnProps } from './types';

/**
 * Creates a custom row selection column definition for Material React Table.
 * 
 * This column provides:
 * - A header checkbox that can select/deselect all rows across all pages
 * - Individual row checkboxes for per-row selection
 * - Support for indeterminate state when some rows are selected
 * 
 */
export const createCustomRowSelectionColumn = ({
    selectedRows,
    allRowsSelected,
    totalRows,
    totalSelectedRows,
    onRowToggle,
    onSelectAll,
    columnId = 'custom-row-selection-column',
    size = 50,
    header = 'select',
    tooltip = 'Select all rows across all pages',
}: CreateCustomRowSelectionColumnProps): MRT_ColumnDef<any>[] => [
        {
            id: columnId,
            size,
            header,
            enableColumnActions: false,
            enableResizing: false,
            Header: ({ table }: { table: MRT_TableInstance<any> }) => {
                const { rows } = table.getRowModel();
                const someSelected = totalSelectedRows > 0 && totalSelectedRows < totalRows;

                return (
                    <Tooltip
                        title={tooltip}
                        placement="top"
                        arrow
                        slotProps={{
                            tooltip: {
                                sx: { maxWidth: '240px' },
                            },
                        }}
                    >
                        <Checkbox
                            checked={allRowsSelected}
                            indeterminate={someSelected}
                            size="small"
                            onChange={() => {
                                onSelectAll(!allRowsSelected, rows);
                            }}
                        />
                    </Tooltip>
                );
            },
            Cell: ({ row }: { row: MRT_Row<any> }) => {
                const rowId = String(row.id);
                // If allRowsSelected is true, all rows are selected regardless of selectedRows
                // Otherwise, check if this specific row is in selectedRows
                const isChecked = allRowsSelected || !!selectedRows[rowId];

                return (
                    <Checkbox
                        checked={isChecked}
                        size="small"
                        onChange={(e) => {
                            onRowToggle(rowId, e.target.checked);
                        }}
                    />
                );
            },
        },
    ];
